import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Edit3, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";

export function AccessHireTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // { id, name, types: [] }
  const [typeDraft, setTypeDraft] = useState("");
  const [importInfo, setImportInfo] = useState("");
  const fileRef = useRef();
  const confirm = useConfirm();

  const load = () => api.get("/access-hire-products").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ name: "", types: [] }); setTypeDraft(""); };
  const startEdit = (p) => { setEditing({ ...p, types: [...(p.types || [])] }); setTypeDraft(""); };
  const cancel = () => { setEditing(null); setTypeDraft(""); };

  const addType = () => {
    const t = typeDraft.trim();
    if (!t) return;
    if (editing.types.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTypeDraft("");
      return;
    }
    setEditing({ ...editing, types: [...editing.types, t] });
    setTypeDraft("");
  };
  const removeType = (t) => setEditing({ ...editing, types: editing.types.filter((x) => x !== t) });

  const save = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name required");
    try {
      if (editing.id) {
        await api.put(`/access-hire-products/${editing.id}`, { name, types: editing.types });
      } else {
        await api.post("/access-hire-products", { name, types: editing.types });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({ title: "Delete this access-hire product?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/access-hire-products/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/access-hire-products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skip = data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"} skipped` : "";
      setImportInfo(`Imported · ${data.inserted} new${skip}${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = "name,types\nCherry Picker,26ft electric|33ft diesel\nScaffold Tower,Single|Double\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "access-hire-template.csv";
    a.click();
  };

  const downloadList = async () => {
    try {
      const res = await api.get("/access-hire-products/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "access-hire-products.csv";
      a.click();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export");
    }
  };

  return (
    <div className="qb-card" data-testid="access-hire-tab">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="qb-section-title text-base">Access Hire Products</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Each product (Scissor Lift, Boom Lift, Scaffold Tower…) has its own list of valid types (Electric / Diesel / Single / Double…).
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={startNew}
            className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
            data-testid="access-add-product-button"
          >
            <Plus size={14} /> Add product
          </button>
          <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="access-import-button">
            <Upload size={14} /> Import CSV
          </button>
          <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="access-export-button">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="access-template-button">
            <Download size={14} /> Template
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        </div>
        {importInfo && <span className="text-xs text-slate-500 basis-full">{importInfo}</span>}
      </div>
      <div className="p-5 space-y-3">
        {items.length === 0 && !editing && (
          <div className="text-center text-slate-400 text-sm py-8">No access hire products yet.</div>
        )}
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2" data-testid={`access-item-${p.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900">{p.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {(p.types || []).length === 0 ? <span className="italic">No types configured</span> : p.types.join(" · ")}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => startEdit(p)} className="h-8 w-8 inline-flex items-center justify-center text-slate-500 hover:text-brand-blue" aria-label="Edit"><Edit3 size={14} /></button>
              <button onClick={() => remove(p.id)} className="h-8 w-8 inline-flex items-center justify-center text-slate-400 hover:text-red-600" aria-label="Delete"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}

        {editing && (
          <div className="border-2 border-brand-blue rounded-lg p-4 space-y-3 mt-2" data-testid="access-editor">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Product name</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="qb-input"
                placeholder="e.g. Cherry Picker"
                data-testid="access-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Types (e.g. Electric / Diesel)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {editing.types.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-semibold" data-testid={`access-type-chip-${t}`}>
                    {t}
                    <button onClick={() => removeType(t)} className="hover:text-red-600" aria-label="Remove type"><X size={12} /></button>
                  </span>
                ))}
                {editing.types.length === 0 && <span className="text-xs text-slate-400">No types yet</span>}
              </div>
              <div className="flex gap-2">
                <input
                  value={typeDraft}
                  onChange={(e) => setTypeDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addType(); } }}
                  className="qb-input flex-1"
                  placeholder="Type a value and press Enter"
                  data-testid="access-type-input"
                />
                <button onClick={addType} className="h-10 px-3 border border-slate-300 hover:border-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1" data-testid="access-add-type-button">
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={cancel} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold">Cancel</button>
              <button onClick={save} className="h-9 px-4 bg-brand-blue text-white hover:bg-brand-blue-dark text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="access-save-button">
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
